/**
 * Remote Subscription Manager
 *
 * Manages subscriptions to remote participants' audio/video tracks.
 * Handles:
 * - Proximity-based subscription (bubble members, as LiveKit identities)
 * - Active speaker prioritization
 * - Video subscription limits
 * - Fallback subscriptions when no proximity data
 *
 * Playback volume policy (zones, bubbles, distance) is owned by the volume
 * authority (game/volumeManager.ts). This manager only exposes
 * setParticipantVolume() as its write path and muteAllRemote() as the
 * fail-closed DND cutoff; it must not derive volumes itself.
 */

import type { Room, RemoteParticipant, RemoteTrackPublication } from 'livekit-client';
import type { Disposable, Unsubscribe } from './types';
import { AVLogger } from '../AVLogger';
import { onBubbleMembersUpdate, emitAudioTracksChanged, onSameMapIdentitiesUpdate } from '../../lib/avEvents';
import {
  listPublications,
  readPubKind,
  readPubSource,
  type TrackPublicationLike,
  type TrackLike,
} from '../../types/livekit';

export interface SubscriptionManagerConfig {
  maxVideoSubscriptions: number;
  videoRetentionMs: number;
  fallbackIntervalMs: number;
  maxAudioSubscriptions: number;
}

export interface SubscriptionManagerDeps {
  getRoom: () => Room | null;
  isSignalOpen: () => boolean;
  isDND: () => boolean;
}

export class SubscriptionManager implements Disposable {
  private _desiredParticipants: string[] = [];
  private _activeSpeakers: string[] = [];
  private _subscriptionStates: Map<string, { audio: boolean; video: boolean }> = new Map();
  private _lastVideoOnAt: Map<string, number> = new Map();
  private _lastApplyKey: string | null = null;
  private _lastProximityAt = 0;

  // Timers
  private _fallbackTimer: ReturnType<typeof setInterval> | null = null;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Cleanup
  private _unsubscribeBubble: Unsubscribe | null = null;
  private _unsubscribeSameMap: Unsubscribe | null = null;
  private _disposed = false;

  // Map-aware filtering
  private _sameMapIdentities: Set<string> = new Set();

  constructor(
    private readonly config: SubscriptionManagerConfig,
    private readonly deps: SubscriptionManagerDeps,
  ) {}

  // ============================================================================
  // Public API
  // ============================================================================

  get desiredParticipants(): readonly string[] {
    return this._desiredParticipants;
  }

  get activeSpeakers(): readonly string[] {
    return this._activeSpeakers;
  }

  /**
   * Start listening for bubble updates and managing subscriptions
   */
  start(): void {
    if (this._disposed) return;

    // Listen for proximity-based bubble updates (LiveKit identities).
    // Volume is not touched here: bubble attenuation is part of the single
    // pair-volume computation in the volume authority.
    this._unsubscribeBubble = onBubbleMembersUpdate((ids: string[]) => {
      this._lastProximityAt = Date.now();
      this.handleDesiredIdsUpdate(ids);
    });

    // Listen for same-map identity updates
    this._unsubscribeSameMap = onSameMapIdentitiesUpdate((ids: string[]) => {
      this._sameMapIdentities = new Set(ids);
      this.applySubscriptions();
    });

    // Fallback: Subscribe to some audio if no proximity data
    this.startFallbackTimer();

    AVLogger.debug('subscriptions.started');
  }

  /**
   * Stop managing subscriptions
   */
  stop(): void {
    this._unsubscribeBubble?.();
    this._unsubscribeBubble = null;

    this._unsubscribeSameMap?.();
    this._unsubscribeSameMap = null;
    this._sameMapIdentities.clear();

    this.stopFallbackTimer();
    this.clearDebounceTimer();

    this._desiredParticipants = [];
    this._activeSpeakers = [];
    this._subscriptionStates.clear();
    this._lastVideoOnAt.clear();
    this._lastApplyKey = null;

    AVLogger.debug('subscriptions.stopped');
  }

  /**
   * Update active speakers list (called from room event)
   */
  setActiveSpeakers(speakers: RemoteParticipant[]): void {
    this._activeSpeakers = speakers.map((p) => String(p.identity || '')).filter(Boolean);
    this.applySubscriptions();
  }

  /**
   * Force re-apply subscriptions (e.g., after reconnect)
   */
  forceApply(): void {
    this._lastApplyKey = null;
    // Clear per-track dedup state so re-published tracks get subscribed.
    // setSubscribed() has its own isCurrentlySubscribed check to avoid thrashing.
    this._subscriptionStates.clear();
    this.applySubscriptions();
  }

  /**
   * Set participant volume (used for bubble attenuation and DND)
   */
  setParticipantVolume(identity: string, volume: number): void {
    const room = this.deps.getRoom();
    if (!room) return;

    try {
      const participant = this.findParticipantByIdentity(room, identity);
      if (!participant) return;

      const publications = listPublications(participant);
      for (const pub of publications) {
        const track = pub.track as TrackLike | undefined | null;
        if (!track) continue;

        const kind = readPubKind(pub);
        const isAudio = kind === 'audio' || (kind == null && typeof track.setVolume === 'function');
        if (!isAudio) continue;
        if (typeof track.setVolume !== 'function') continue;
        track.setVolume(Math.max(0, Math.min(1, volume)));
      }
    } catch (error) {
      AVLogger.warn('subscription.volume.error', { identity, error: String(error) });
    }
  }

  /**
   * Mute all remote audio (for DND)
   */
  muteAllRemote(): void {
    const room = this.deps.getRoom();
    if (!room) return;

    try {
      const participants: RemoteParticipant[] = Array.from(room.remoteParticipants.values());
      for (const p of participants) {
        const publications = listPublications(p);
        for (const pub of publications) {
          const track = pub.track as TrackLike | undefined | null;
          if (!track) continue;

          const kind = readPubKind(pub);
          const isAudio = kind === 'audio' || (kind == null && typeof track.setVolume === 'function');
          if (isAudio && typeof track.setVolume === 'function') {
            track.setVolume(0);
          }
        }
      }
    } catch (error) {
      AVLogger.warn('subscription.mute_all.error', { error: String(error) });
    }
  }

  /**
   * Hand remote audio volumes back to the volume authority (after DND).
   *
   * Intentionally writes no volumes: tracks stay at the muted level from
   * muteAllRemote() (fail-closed) until the volume authority recomputes
   * zone/bubble-aware volumes in response to the audio-tracks-changed event.
   */
  restoreAllRemote(): void {
    try {
      emitAudioTracksChanged();
    } catch (error) {
      AVLogger.warn('subscription.restore_all.error', { error: String(error) });
    }
  }

  /**
   * Ensure audio subscriptions for all participants (up to limit)
   */
  ensureAudioSubscriptions(maxCount: number = 32): void {
    const room = this.deps.getRoom();
    if (!room) return;
    if (this.deps.isDND()) return;
    if (!this.deps.isSignalOpen()) return;

    try {
      const participants: RemoteParticipant[] = Array.from(room.remoteParticipants.values());
      let count = 0;

      for (const p of participants) {
        if (count >= maxCount) break;
        const identity = String(p.identity || '');
        if (!this.isOnSameMap(identity)) continue;

        const publications = listPublications(p);
        for (const pub of publications) {
          const kind = readPubKind(pub);
          if (kind === 'audio') {
            this.setSubscribed(pub as unknown as RemoteTrackPublication, true);
            count++;
          }
        }
      }

      AVLogger.debug('subscription.ensure_audio', { count, maxCount });
    } catch (error) {
      AVLogger.warn('subscription.ensure_audio.error', { error: String(error) });
    }
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this.stop();
  }

  // ============================================================================
  // Private: Subscription Logic
  // ============================================================================

  private handleDesiredIdsUpdate(ids: string[]): void {
    const normalized = Array.from(new Set(ids.map((id) => String(id || '')))).sort();
    const prev = JSON.stringify(this._desiredParticipants.slice().sort());
    const next = JSON.stringify(normalized);

    if (prev === next) return;

    this._desiredParticipants = normalized;

    // Debounce apply
    this.clearDebounceTimer();
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      this.applySubscriptions();
    }, 200);
  }

  private applySubscriptions(): void {
    const room = this.deps.getRoom();
    if (!room) return;
    if (this.deps.isDND()) return;
    if (!this.deps.isSignalOpen()) return;

    const participants: RemoteParticipant[] = Array.from(room.remoteParticipants.values());
    const participantCount = participants.length;

    // Count video publications to detect track changes (e.g. screen share restart)
    let videoPublicationCount = 0;
    for (const p of participants) {
      const publications = listPublications(p);
      for (const pub of publications) {
        const kind = readPubKind(pub);
        if (kind === 'video') videoPublicationCount++;
      }
    }

    // Build deduplication key
    const key = [
      JSON.stringify(this._desiredParticipants),
      JSON.stringify(this._activeSpeakers.slice(0, this.config.maxVideoSubscriptions)),
      participantCount,
      videoPublicationCount,
    ].join('|');

    if (key === this._lastApplyKey) return;
    this._lastApplyKey = key;

    AVLogger.debug('subscription.apply', {
      desiredCount: this._desiredParticipants.length,
      speakerCount: this._activeSpeakers.length,
      participantCount,
    });

    try {
      const desiredSet = new Set(this._desiredParticipants);
      for (const p of participants) {
        const identity = String(p.identity || '');
        const shouldSubscribe = desiredSet.has(identity);
        const publications = listPublications(p);

        for (const pub of publications) {
          const kind = readPubKind(pub);
          const source = readPubSource(pub);

          if (kind === 'audio') {
            const onSameMap = this.isOnSameMap(identity);
            this.setDesired(pub as unknown as RemoteTrackPublication, identity, 'audio', onSameMap);
          }

          if (kind === 'video') {
            // Subscribe to video based on priority
            const isScreenShare = source === 'screen_share';
            // Camera visibility follows the same proximity/bubble audience as
            // ordinary voice. The old small-room/active-speaker exceptions
            // made every camera visible across the entire room, even when the
            // corresponding audio was muted by distance.
            const shouldHaveVideo = isScreenShare || this.isOnSameMap(identity);

            this.setDesired(pub as unknown as RemoteTrackPublication, identity, 'video', shouldHaveVideo);
          }
        }
      }

      emitAudioTracksChanged();
    } catch (error) {
      AVLogger.error('subscription.apply.error', { error: String(error) });
    }
  }

  private setDesired(pub: RemoteTrackPublication, identity: string, kind: 'audio' | 'video', should: boolean): void {
    const key = `${identity}:${kind}`;
    const current = this._subscriptionStates.get(key);

    // Video hysteresis: don't turn off video too quickly
    if (kind === 'video' && !should) {
      const lastOn = this._lastVideoOnAt.get(identity) || 0;
      if (Date.now() - lastOn < this.config.videoRetentionMs) {
        return;
      }
    }

    if (kind === 'video' && should) {
      this._lastVideoOnAt.set(identity, Date.now());
    }

    // Update state if changed
    if (current?.[kind] !== should) {
      const state = this._subscriptionStates.get(key) || { audio: false, video: false };
      state[kind] = should;
      this._subscriptionStates.set(key, state);
    }

    // Always apply the subscription: a new publication for the same identity
    // (e.g. screen share restart) needs subscribing even when state unchanged.
    // setSubscribed() has its own guard to avoid redundant operations.
    this.setSubscribed(pub, should);
  }

  private setSubscribed(pub: RemoteTrackPublication, should: boolean): void {
    try {
      const isCurrentlySubscribed = this.isSubscribed(pub);
      if (isCurrentlySubscribed === should) return;
      if (!this.deps.isSignalOpen()) return;

      const pubLike = pub as unknown as TrackPublicationLike;
      if (typeof pubLike.setSubscribed === 'function') {
        pubLike.setSubscribed(should);
      }
    } catch (error) {
      AVLogger.warn('subscription.set.error', { error: String(error) });
    }
  }

  private isSubscribed(pub: RemoteTrackPublication): boolean {
    try {
      const pubLike = pub as unknown as TrackPublicationLike;
      if (typeof pubLike.isSubscribed === 'boolean') return pubLike.isSubscribed;
      if (typeof pubLike.subscribed === 'boolean') return pubLike.subscribed;
      return !!pubLike.track;
    } catch {
      return false;
    }
  }

  // ============================================================================
  // Private: Fallback Timer
  // ============================================================================

  private startFallbackTimer(): void {
    if (this._fallbackTimer) return;

    this._fallbackTimer = setInterval(() => {
      if (this._disposed) return;
      if (this.deps.isDND()) return;

      // Only use fallback if no proximity data recently
      const timeSinceProximity = Date.now() - this._lastProximityAt;
      if (timeSinceProximity < 3000) return;

      this.applyFallbackSubscriptions();
    }, this.config.fallbackIntervalMs);
  }

  private stopFallbackTimer(): void {
    if (this._fallbackTimer) {
      clearInterval(this._fallbackTimer);
      this._fallbackTimer = null;
    }
  }

  private applyFallbackSubscriptions(): void {
    const room = this.deps.getRoom();
    if (!room) return;
    if (!this.deps.isSignalOpen()) return;

    try {
      const participants: RemoteParticipant[] = Array.from(room.remoteParticipants.values());
      const sameMapParticipants = participants.filter((p) => this.isOnSameMap(String(p.identity || '')));
      const chosen = sameMapParticipants.slice(0, this.config.maxAudioSubscriptions);

      for (const p of participants) {
        const identity = String(p.identity || '');
        const shouldSubscribe = chosen.includes(p);
        const publications = listPublications(p);

        for (const pub of publications) {
          const kind = readPubKind(pub);
          if (kind === 'audio') {
            this.setDesired(pub as unknown as RemoteTrackPublication, identity, 'audio', shouldSubscribe);
          }
        }
      }
    } catch (error) {
      AVLogger.warn('subscription.fallback.error', { error: String(error) });
    }
  }

  private clearDebounceTimer(): void {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }

  // ============================================================================
  // Private: Helpers
  // ============================================================================

  private isOnSameMap(identity: string): boolean {
    if (this._sameMapIdentities.size === 0) return true; // No map context → allow all (fallback)
    return this._sameMapIdentities.has(identity);
  }

  private findParticipantByIdentity(room: Room, identity: string): RemoteParticipant | null {
    const participants: RemoteParticipant[] = Array.from(room.remoteParticipants.values());
    return participants.find((p) => p.identity === identity) || room.remoteParticipants.get(identity) || null;
  }
}
